import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';

const AuthContext = createContext(null);

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Wraps a promise with a hard timeout so a hanging network call never strands the UI.
const withTimeout = (promise, ms, label = 'op') =>
  Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms)),
  ]);

// Small retry helper (max 3 attempts, 400ms/800ms backoff).
async function withRetry(fn, { attempts = 3, label = 'op' } = {}) {
  let lastErr;
  for (let i = 0; i < attempts; i++) {
    try {
      return await fn(i);
    } catch (e) {
      lastErr = e;
      console.warn(`[auth] ${label} attempt ${i + 1}/${attempts} failed:`, e?.message || e);
      if (i < attempts - 1) await sleep(400 * (i + 1));
    }
  }
  throw lastErr;
}

// Read a persisted supabase session synchronously from localStorage.
// Returns a session-shaped object or null. This avoids the network hop that
// supabase-js triggers during token refresh — critical when the browser<->supabase
// path is slow/flaky (which strands the auth bootstrap otherwise).
function readPersistedSession() {
  try {
    const raw = localStorage.getItem('velvet-os-auth');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // supabase-js v2 stores under { currentSession, expiresAt } OR flat.
    const s = parsed?.currentSession || parsed?.session || parsed;
    if (!s?.access_token || !s?.user) return null;
    // Only trust if not expired.
    const expiresAt = s.expires_at || s.expiresAt;
    if (expiresAt && Number(expiresAt) * 1000 < Date.now()) return null;
    return s;
  } catch (e) {
    console.warn('[auth] readPersistedSession error', e);
    return null;
  }
}

// Fetch the profile row via a direct REST call.
// We intentionally bypass supabase-js here because its internal auth lock can
// deadlock when the token-refresh path times out in flaky networks, which then
// permanently strands every .from().select() call. A plain fetch has no such lock.
async function fetchProfileDirect(userId, accessToken) {
  const url = `${process.env.REACT_APP_SUPABASE_URL}/rest/v1/profiles?id=eq.${encodeURIComponent(userId)}&select=id,email,full_name,role,active&limit=1`;
  const apikey = process.env.REACT_APP_SUPABASE_ANON_KEY;
  const resp = await fetch(url, {
    headers: {
      apikey,
      Authorization: `Bearer ${accessToken}`,
      Accept: 'application/json',
    },
  });
  if (!resp.ok) {
    const body = await resp.text().catch(() => '');
    throw new Error(`profiles HTTP ${resp.status} :: ${body.substring(0, 120)}`);
  }
  const rows = await resp.json();
  return Array.isArray(rows) && rows.length > 0 ? rows[0] : null;
}

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null); // { code, message } | null
  const [bootstrapId, setBootstrapId] = useState(0); // increments each retry, used by consumers to reset timers
  // Sticky flag: Supabase fires event 'PASSWORD_RECOVERY' when the recovery
  // hash is consumed anywhere in the app. We use this to force the /auth/callback
  // set-password form regardless of which URL the user actually landed on.
  const [recoveryPending, setRecoveryPending] = useState(() => {
    try { return sessionStorage.getItem('velvet-recovery-pending') === '1'; } catch { return false; }
  });
  const clearRecoveryPending = useCallback(() => {
    setRecoveryPending(false);
    try {
      sessionStorage.removeItem('velvet-recovery-pending');
      sessionStorage.removeItem('velvet-auth-type');
      sessionStorage.removeItem('velvet-auth-error');
      sessionStorage.removeItem('velvet-auth-access-token');
      sessionStorage.removeItem('velvet-auth-refresh-token');
    } catch { /* ignore */ }
  }, []);
  const loadedUserIdRef = useRef(null);
  const mountedRef = useRef(true);

  const loadProfile = useCallback(async (userId, accessToken) => {
    if (!userId) { setProfile(null); loadedUserIdRef.current = null; return; }
    if (loadedUserIdRef.current === userId && profile) return;
    // Fall back to persisted token if the caller (e.g. onAuthStateChange) didn't provide one.
    let token = accessToken;
    if (!token) {
      const persisted = readPersistedSession();
      token = persisted?.access_token || null;
    }
    if (!token) {
      // No usable token — do not blow the profile fetch. Just skip; retryAuth or a new
      // onAuthStateChange event will re-run with a real token.
      console.warn('[auth] loadProfile skipped: no access token available yet');
      return;
    }
    loadedUserIdRef.current = userId;
    try {
      const result = await withRetry(async () => {
        return await withTimeout(fetchProfileDirect(userId, token), 5000, 'profiles.fetch');
      }, { attempts: 3, label: 'loadProfile' });
      if (!mountedRef.current) return;
      if (result) {
        setProfile(result);
        setAuthError(null);
      } else {
        loadedUserIdRef.current = null;
        setAuthError({ code: 'PROFILE_NOT_FOUND', message: 'Your account has no profile row. Please contact an admin.' });
      }
    } catch (e) {
      console.warn('[auth] loadProfile permanently failed', e);
      if (!mountedRef.current) return;
      loadedUserIdRef.current = null;
      setAuthError({ code: 'PROFILE_FETCH_FAILED', message: 'Could not reach Velvet OS servers. Please check your connection.' });
    }
  }, [profile]);

  const bootstrap = useCallback(async () => {
    setAuthError(null);
    setLoading(true);
    setBootstrapId((n) => n + 1);
    try {
      // Fast path: try supabase.auth.getSession() with a tight timeout.
      // Slow path: fall back to localStorage-persisted session (still valid, not expired)
      // so hard-navs work even when supabase's internal token-refresh network call hangs.
      let sess = null;
      try {
        const { data, error } = await withTimeout(supabase.auth.getSession(), 2500, 'auth.getSession');
        if (error) throw error;
        sess = data?.session || null;
      } catch (e) {
        console.warn('[auth] getSession fast-path failed, using localStorage fallback:', e?.message || e);
        sess = readPersistedSession();
        if (sess) console.info('[auth] recovered session from localStorage');
      }
      if (!mountedRef.current) return;
      setSession(sess);
      if (sess?.user) {
        await loadProfile(sess.user.id);
      }
    } catch (e) {
      console.warn('[auth] bootstrap failed', e);
      if (mountedRef.current) {
        setSession(null);
        setProfile(null);
        setAuthError({ code: 'SESSION_FETCH_FAILED', message: 'Could not verify your session. Please retry or sign in again.' });
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [loadProfile]);

  useEffect(() => {
    mountedRef.current = true;
    bootstrap();
    const { data: sub } = supabase.auth.onAuthStateChange(async (evt, sess) => {
      if (!mountedRef.current) return;
      // Persist the sticky recovery flag as soon as Supabase tells us the URL
      // hash was a recovery link — this survives navigations and re-mounts.
      if (evt === 'PASSWORD_RECOVERY') {
        setRecoveryPending(true);
        try { sessionStorage.setItem('velvet-recovery-pending', '1'); } catch { /* ignore */ }
      }
      setSession(sess);
      if (sess?.user) {
        await loadProfile(sess.user.id, sess.access_token);
      } else {
        setProfile(null);
        loadedUserIdRef.current = null;
        if (evt === 'SIGNED_OUT') {
          setAuthError(null);
          clearRecoveryPending();
        }
      }
    });
    return () => {
      mountedRef.current = false;
      sub.subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signIn = async (email, password) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  };

  const signOut = async () => {
    loadedUserIdRef.current = null;
    setProfile(null);
    setAuthError(null);
    try { await supabase.auth.signOut(); } catch (e) { console.warn('[auth] signOut error', e); }
    setSession(null);
    setLoading(false);
  };

  // Force clear local session and hard-redirect to /login.
  // This is the "escape hatch" used by the visible error screen to guarantee
  // the user is never stranded, regardless of any in-flight bootstrap retry loop.
  // NOTE: we do NOT await the supabase call — if the SDK is stuck (which is why
  // the user landed here in the first place), awaiting would re-hang. Instead we
  // wipe localStorage directly and hard-reload the browser to /login.
  const clearSessionAndLogout = useCallback(() => {
    // Fire-and-forget local signOut; ignore any pending promise.
    try { supabase.auth.signOut({ scope: 'local' }); } catch (e) { console.warn('[auth] local signOut error', e); }
    // Directly nuke all supabase-related localStorage keys so the next page load starts clean.
    try {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && (k === 'velvet-os-auth' || k.startsWith('sb-') || k.includes('supabase'))) {
          keysToRemove.push(k);
        }
      }
      keysToRemove.forEach((k) => localStorage.removeItem(k));
    } catch (_) { /* ignore */ }
    // Hard reset: guarantees no stale React state or pending retry keeps the user stuck.
    window.location.replace('/login');
  }, []);

  const retryAuth = useCallback(() => {
    // Reset in-memory error before re-running bootstrap so the fallback UI
    // hides immediately and the loading spinner takes over.
    setAuthError(null);
    bootstrap();
  }, [bootstrap]);

  return (
    <AuthContext.Provider
      value={{
        session,
        profile,
        loading,
        authError,
        bootstrapId,
        signIn,
        signOut,
        retryAuth,
        clearSessionAndLogout,
        recoveryPending,
        clearRecoveryPending,
        role: profile?.role,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const c = useContext(AuthContext);
  if (!c) throw new Error('useAuth must be inside AuthProvider');
  return c;
};

export const canSeeCosts = (role) => role === 'admin' || role === 'manager' || role === 'purchasing';
export const isDriver = (role) => role === 'driver';
export const isDesigner = (role) => role === 'designer';
